import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useGetLeaveTypesQuery } from "../../services/settings.service";
import { useLazySearchEmployeesQuery } from "../../services/employee.service";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

// Validation schema
const leaveSchema = yup.object().shape({
  employee_id: yup.string().when('$isAdmin', {
    is: true,
    then: (schema) => schema.required("Employee is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  leave_type_id: yup.string().required("Leave type is required"),
  start_date: yup.date()
    .required("Start date is required")
    .typeError("Start date must be a valid date"),
  end_date: yup.date()
    .required("End date is required")
    .typeError("End date must be a valid date")
    .min(yup.ref('start_date'), "End date must be after start date"),
  number_of_days: yup.number()
    .required("Number of days is required")
    .min(0.5, "Minimum 0.5 day required")
    .typeError("Number of days must be a number"),
  reason: yup.string()
    .required("Leave reason is required")
    .min(10, "Reason must be at least 10 characters")
    .max(500, "Reason must not exceed 500 characters"),
});

const EmployeeLeaveModelPopup = ({ item, onSubmit, isLoading }) => {
  const { user } = useSelector((state) => state.user);
  const isAdmin = user?.role !== "Employee";
  const modalRef = useRef(null);

  // Fetch leave types
  const { data: leaveTypesData } = useGetLeaveTypesQuery();
  const [searchEmployees, { data: employeesData }] = useLazySearchEmployeesQuery();

  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(leaveSchema),
    context: { isAdmin },
    defaultValues: {
      employee_id: "",
      leave_type_id: "",
      start_date: null,
      end_date: null,
      number_of_days: 0,
      reason: "",
    },
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");

  // Calculate number of days when dates change
  useEffect(() => {
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const days = end.diff(start, "day") + 1; // +1 to include both start and end date
      setValue("number_of_days", days > 0 ? days : 0);
    } else {
      setValue("number_of_days", 0);
    }
  }, [startDate, endDate, setValue]);

  // Populate form when editing
  useEffect(() => {
    if (item) {
      reset({
        employee_id: item.employee_id || "",
        leave_type_id: item.leave_type_id || "",
        start_date: item.start_date ? new Date(item.start_date) : null,
        end_date: item.end_date ? new Date(item.end_date) : null,
        number_of_days: item.number_of_days || 0,
        reason: item.reason || "",
      });
    } else {
      // Reset form for new leave
      reset({
        employee_id: isAdmin ? "" : user?.emp_id,
        leave_type_id: "",
        start_date: null,
        end_date: null,
        number_of_days: 0,
        reason: "",
      });
    }
  }, [item, reset, isAdmin, user]);

  // Employee search with debounce
  useEffect(() => {
    if (employeeSearchTerm && employeeSearchTerm.length >= 2) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        searchEmployees({ q: employeeSearchTerm, branch_id: "" });
        setIsSearching(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [employeeSearchTerm, searchEmployees]);

  // Transform leave types for react-select
  const leaveTypeOptions = leaveTypesData?.data?.map((type) => ({
    value: type.id,
    label: `${type.name} (${type.days_allowed} days)`,
    days_allowed: type.days_allowed,
  })) || [];

  // Transform employees for react-select
  const employeeOptions = employeesData?.data?.map((emp) => ({
    value: emp.id,
    label: `${emp.first_name} ${emp.last_name} (${emp.emp_id})`,
  })) || [];

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#008a42" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#008a42",
      },
    }),
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#008a42" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 1px #008a42" : provided.boxShadow,
      "&:hover": {
        borderColor: "#008a42",
      },
    }),
  };

  const onFormSubmit = async (data) => {
    try {
      const formattedData = {
        ...data,
        employee_id: isAdmin ? data.employee_id : user?.emp_id,
        start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
        end_date: dayjs(data.end_date).format("YYYY-MM-DD"),
        status: "Pending",
      };

      // If editing, include the ID
      if (item?.id) {
        formattedData.id = item.id;
      }

      await onSubmit(formattedData);

      // Close modal after successful submission
      const modal = window.bootstrap.Modal.getInstance(modalRef.current);
      if (modal) {
        modal.hide();
      }
    } catch (error) {
      console.error("Error submitting leave:", error);
    }
  };

  return (
    <div
      id="add_leave"
      className="modal custom-modal fade"
      role="dialog"
      ref={modalRef}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {item ? "Edit Leave" : "Add Leave"}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit(onFormSubmit)}>
              {/* Employee Dropdown (Only for Admin/HR) */}
              {isAdmin && (
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Employee <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="employee_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={employeeOptions}
                        placeholder="Search employee by name or ID..."
                        styles={customStyles}
                        isClearable
                        isLoading={isSearching}
                        onInputChange={(value) => setEmployeeSearchTerm(value)}
                        value={employeeOptions.find((opt) => opt.value === field.value) || null}
                        onChange={(selectedOption) => field.onChange(selectedOption?.value || "")}
                      />
                    )}
                  />
                  {errors.employee_id && (
                    <span className="text-danger small">
                      {errors.employee_id.message}
                    </span>
                  )}
                </div>
              )}

              {/* Leave Type */}
              <div className="input-block mb-3">
                <label className="col-form-label">
                  Leave Type <span className="text-danger">*</span>
                </label>
                <Controller
                  name="leave_type_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={leaveTypeOptions}
                      placeholder="Select Leave Type"
                      styles={customStyles}
                      isClearable
                      value={leaveTypeOptions.find((opt) => opt.value === field.value) || null}
                      onChange={(selectedOption) => field.onChange(selectedOption?.value || "")}
                    />
                  )}
                />
                {errors.leave_type_id && (
                  <span className="text-danger small">
                    {errors.leave_type_id.message}
                  </span>
                )}
              </div>

              {/* Start Date */}
              <div className="input-block mb-3">
                <label className="col-form-label">
                  From <span className="text-danger">*</span>
                </label>
                <div className="cal-icon">
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        className={`form-control datetimepicker ${
                          errors.start_date ? "is-invalid" : ""
                        }`}
                        dateFormat="dd-MM-yyyy"
                        minDate={new Date()}
                        placeholderText="Select start date"
                      />
                    )}
                  />
                </div>
                {errors.start_date && (
                  <span className="text-danger small">
                    {errors.start_date.message}
                  </span>
                )}
              </div>

              {/* End Date */}
              <div className="input-block mb-3">
                <label className="col-form-label">
                  To <span className="text-danger">*</span>
                </label>
                <div className="cal-icon">
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        className={`form-control datetimepicker ${
                          errors.end_date ? "is-invalid" : ""
                        }`}
                        dateFormat="dd-MM-yyyy"
                        minDate={startDate || new Date()}
                        placeholderText="Select end date"
                      />
                    )}
                  />
                </div>
                {errors.end_date && (
                  <span className="text-danger small">
                    {errors.end_date.message}
                  </span>
                )}
              </div>

              {/* Number of Days */}
              <div className="input-block mb-3">
                <label className="col-form-label">
                  Number of days <span className="text-danger">*</span>
                </label>
                <Controller
                  name="number_of_days"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      className={`form-control ${
                        errors.number_of_days ? "is-invalid" : ""
                      }`}
                      type="number"
                      step="0.5"
                      readOnly={startDate && endDate}
                    />
                  )}
                />
                {errors.number_of_days && (
                  <span className="text-danger small">
                    {errors.number_of_days.message}
                  </span>
                )}
              </div>

              {/* Leave Reason */}
              <div className="input-block mb-3">
                <label className="col-form-label">
                  Leave Reason <span className="text-danger">*</span>
                </label>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      rows={4}
                      className={`form-control ${
                        errors.reason ? "is-invalid" : ""
                      }`}
                      placeholder="Enter reason for leave request (minimum 10 characters)"
                    />
                  )}
                />
                {errors.reason && (
                  <span className="text-danger small">
                    {errors.reason.message}
                  </span>
                )}
                <small className="text-muted">
                  {watch("reason")?.length || 0}/500 characters
                </small>
              </div>

              {/* Submit Button */}
              <div className="submit-section">
                <button
                  className="btn btn-primary submit-btn"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    item ? "Update Leave" : "Submit Leave"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLeaveModelPopup;
